import { useState, type DragEvent } from 'react';

/** Moves `id` before `targetId` (or by one step with `delta`). Pure, for the draft order lists of 06 and 09. */
export function move<T extends { id: string }>(items: T[], id: string, to: { targetId: string } | { delta: -1 | 1 }): T[] {
  const from = items.findIndex((i) => i.id === id);
  if (from < 0) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  let index = 'delta' in to ? from + to.delta : next.findIndex((i) => i.id === to.targetId);
  if ('targetId' in to && index >= 0 && from <= index) index += 1;
  if (index < 0) index = 0;
  next.splice(Math.min(index, next.length), 0, moved!);
  return next;
}

/** Native HTML drag and drop on table rows, plus the same moves for the keyboard (up / down buttons). */
export function useRowDrag<T extends { id: string }>(items: T[], onChange: (next: T[]) => void) {
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  return {
    dragged,
    over,
    rowProps: (id: string) => ({
      draggable: true,
      onDragStart: (event: DragEvent) => {
        setDragged(id);
        event.dataTransfer.effectAllowed = 'move';
      },
      onDragOver: (event: DragEvent) => {
        event.preventDefault();
        setOver(id);
      },
      onDragEnd: () => {
        setDragged(null);
        setOver(null);
      },
      onDrop: (event: DragEvent) => {
        event.preventDefault();
        if (dragged && dragged !== id) onChange(move(items, dragged, { targetId: id }));
        setDragged(null);
        setOver(null);
      },
    }),
    step: (id: string, delta: -1 | 1) => onChange(move(items, id, { delta })),
  };
}
